import { Car } from 'lucide-preact';
import { route } from 'preact-router';
import { useState } from 'preact/hooks';
import { StarRating } from '../../components/StarRating.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type CarFuel, FuelGauge } from '../../components/ui/FuelGauge.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { TripContextStrip } from '../../components/ui/datetime/TripContextStrip.js';
import { goneMessage, lookupFailedMessage } from '../../components/ui/resultWording.js';
import { useAuth } from '../../context/AuthContext.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useOccupiedWindows } from '../../hooks/useOccupiedWindows.js';
import { useSignInRedirect } from '../../hooks/useSignInRedirect.js';
import { api } from '../../services/api.js';
import { formatMinor } from '../../utils/money.js';
import { mytDate } from '../../utils/mytTime.js';
import { type Promo, daysWords, promoHoursWords } from '../../utils/promos.js';
import { parseCarriedDate, tripQueryString } from '../../utils/tripQuery.js';
import { GOOD_TO_KNOW_ITEMS, SPEC_ICONS } from './carDetailContent.js';

/** How many reviews the car page shows. 363: the newest 3; "See all" opens the rest (design 2c). */
const REVIEW_PAGE_SIZE = 3;
/** 363 (FR-012): the 5-to-1 split says something only from this many reviews. */
const SPLIT_FROM = 3;

interface CarReview {
  id: string;
  rating: number;
  comment?: string;
  created_at: string;
  author: { name: string } | null;
}

interface ReviewListResponse {
  items: CarReview[];
  total: number;
}

interface CarDetail {
  _id: string;
  name: string;
  /** 052: the display label — brand + model. Prefer this over `name`, which is a
   * `·`-joined specification dump that also goes stale after an admin edit. */
  label?: string;
  description?: string;
  /** 052: denormalized pickup point, already present in the payload before this feature
   * and simply never rendered (gap-list C3).
   * 401 (#401): `name` — the operator-given "Location Name" ("UMSKAL") — is the PRIMARY
   * label; `city_name` ("Victoria") is a secondary line, never the headline (customers read
   * the city as where the car physically sits). Resolved server-side (cars/location-names.ts),
   * absent only on a cached/older response. */
  location?: {
    name?: string;
    city_name?: string;
    address_line_1?: string;
    country_name?: string;
  };
  /** 052: the renting business. Null when it cannot be resolved — then no line renders. */
  supplier?: { id: string; company_name: string } | null;
  transmission: string;
  fuel_type: string;
  seat_capacity: number;
  year: number;
  color?: string;
  // 147: already public on the wire, never rendered until now (same gap shape as 146's own).
  category_name?: string;
  // 147: reverses 146's own withholding note below — the plate is public now, by explicit
  // user decision, and shown pre-booking too (it already showed post-booking, on the
  // customer's own booking, per FR-016).
  plate_number?: string;
  // 050: resolved catalog features (id + name + optional icon), in operator order.
  features?: Array<{ id: string; name: string; icon?: string }>;
  images?: Array<{ url: string; is_primary?: boolean }>;
  // 050: browsing display rate. Null when the car has no usable rate card — then no
  // price is rendered at all. The booking form's live quote remains authoritative.
  pricing?: {
    currency_symbol: string;
    per_hour_minor: number | null;
    per_day_minor: number | null;
    // 146 (US2): already stored on the rate card, only just threaded through to this display
    // summary. `null`/absent means no minimum configured — never a placeholder (FR-007).
    min_rental_minutes?: number | null;
    min_rental_days?: number | null;
    /** 400 (#1354): the rate card's time-of-day promos; absent on older payloads. */
    promos?: Promo[];
  } | null;
  // 057: how previous renters rated this car. Null when nobody has — then the page shows
  // no rating and no reviews section, and asks the server for neither.
  /** 408 (#1434): present only for a car on Cartrack with a reading. */
  fuel?: CarFuel;
  rating?: {
    average: number;
    count: number;
    /** 363: how many reviews gave each star (detail only). */
    distribution?: Record<'1' | '2' | '3' | '4' | '5', number>;
  } | null;
  // 146 (US2): already public on the wire (039/052) and never rendered until now. Tagged
  // 3-state — only `status: 'open'` carries a window; `'unrestricted'`/`'blocked'` render
  // nothing on this customer-facing page (data-model.md).
  effective_operating_window?:
    | { status: 'open'; window: { start: string; end: string } }
    | { status: 'unrestricted' }
    | { status: 'blocked' };
}

interface CarDetailProps {
  id?: string;
  path?: string;
  // 126: whatever window the customer already chose while searching, carried onto
  // this page instead of dropped — location_id is deliberately not part of this
  // (research.md D1).
  pickup_at?: string;
  return_at?: string;
}

export function CarDetailPage({ id, pickup_at, return_at }: CarDetailProps) {
  const goToSignIn = useSignInRedirect();
  const { isAuthenticated } = useAuth();
  const carRead = useFetch<CarDetail>(id ? `/cars/${id}` : null);
  const { data: car, status: carStatus } = carRead;
  // Once a window has been carried, the strip stays put even if editing clears a
  // date — yanking it away mid-edit would be worse than showing an empty field.
  // `pickup_at`/`return_at` never change after mount for this route, so this needs
  // no state of its own.
  const hasCarriedContext = Boolean(pickup_at || return_at);
  // 386 (#1301, FR-015): the strip below lets the customer change the carried window; it greys out the car's other
  // rentals exactly as the booking form does. Read only when the strip is shown.
  const occupied = useOccupiedWindows(hasCarriedContext ? id : null);
  const [tripPickup, setTripPickup] = useState(() => parseCarriedDate(pickup_at));
  const [tripReturn, setTripReturn] = useState(() => parseCarriedDate(return_at));
  // `idle` means no id in the address yet — nothing asked, so nothing to report.
  const loading = carStatus === 'loading' || carStatus === 'idle';
  const [activeImg, setActiveImg] = useState(0);
  // 057: only ask for reviews when the car payload says there are some. Most cars have
  // none, and `useFetch(null)` issues no request at all (FR-016, SC-004).
  const reviewsRead = useFetch<ReviewListResponse>(
    id && car?.rating ? `/reviews?car_id=${id}&limit=${REVIEW_PAGE_SIZE}` : null,
  );
  const { data: reviews } = reviewsRead;
  /**
   * 064: the reviews are a lookup, not this screen's subject. A dead lookup must not hide a car
   * whose details loaded perfectly — it states itself where the reviews live (FR-010, C2.6).
   */
  const reviewsFailed =
    reviewsRead.status !== 'ok' &&
    reviewsRead.status !== 'loading' &&
    reviewsRead.status !== 'idle';

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  // 064: this used to answer EVERY non-car outcome with "Car not found" — so an outage stated
  // that the car does not exist, which is a different and worse claim than "we could not find
  // out". The read's own outcome decides what is said.
  if (carStatus !== 'ok' || !car) {
    return (
      <ResultState
        state={carRead}
        onRetry={carRead.retry}
        goneMessage={goneMessage('car')}
        onwardLabel="Back to search"
        onOnward={() => route('/search')}
        onSignIn={goToSignIn}
      />
    );
  }

  const images = car.images ?? [];
  const symbol = car.pricing?.currency_symbol;
  // Hourly leads (rentals here are mainly hourly); the daily rate supports it.
  const hourlyRate = formatMinor(car.pricing?.per_hour_minor, symbol);
  const dailyRate = formatMinor(car.pricing?.per_day_minor, symbol);
  const reviewItems = reviews?.items ?? [];
  const reviewTotal = reviews?.total ?? 0;
  // 146 (US2, FR-005/FR-007): only `status: 'open'` carries a window — 'unrestricted' and
  // 'blocked' both render nothing (data-model.md).
  // 284 (#334): the window is Malaysia time — labelled explicitly rather than bare numbers, which
  // read as ambiguous (and, before this feature, were actually UTC while looking like local time).
  const operatingWindowText =
    car.effective_operating_window?.status === 'open'
      ? `Pickup available ${car.effective_operating_window.window.start}–${car.effective_operating_window.window.end} (Malaysia time)`
      : null;
  // 146 (US2, FR-006/FR-007): days take precedence when both are somehow set — a day-based
  // minimum is the more common real configuration and the more legible statement.
  const minRentalText =
    typeof car.pricing?.min_rental_days === 'number' && car.pricing.min_rental_days > 0
      ? `Minimum rental: ${car.pricing.min_rental_days} day${car.pricing.min_rental_days === 1 ? '' : 's'}`
      : typeof car.pricing?.min_rental_minutes === 'number' && car.pricing.min_rental_minutes > 0
        ? `Minimum rental: ${car.pricing.min_rental_minutes} minutes`
        : null;

  /**
   * 116: unverified customers never see the booking form. Signed-out visitors are unaffected —
   * `/booking/:id`'s own AuthGuard already sends them to sign in. The identity check is a UX
   * convenience only; `POST /bookings` re-checks server-side regardless, so a failed fetch here
   * fails OPEN to the booking form rather than stranding an approved customer on a flaky network.
   */
  async function handleBookNow() {
    // 126: forwards the strip's CURRENT window — edited or untouched, it's the same
    // state either way (US3) — never the original pickup_at/return_at props.
    const bookingPath = `/booking/${id}${tripQueryString({
      pickup_at: tripPickup?.toISOString(),
      return_at: tripReturn?.toISOString(),
    })}`;
    if (!isAuthenticated) {
      route(bookingPath);
      return;
    }
    try {
      // 379 (#379): an approved-but-expired licence must send the customer to the identity page
      // too — `licence_expired` is computed server-side (users/identity.ts), never re-derived here.
      const identity = await api.get<{ status: string; licence_expired?: boolean }>(
        '/users/me/identity',
      );
      const canBook = identity.status === 'approved' && !identity.licence_expired;
      route(canBook ? bookingPath : '/account/identity?blocked=booking');
    } catch {
      route(bookingPath);
    }
  }

  return (
    <div className="bg-bg">
      {/* 208 (#308): a customer reaching this page from search had no way to step back. A
          generic title, not the car's own label — that already has its own, more prominent
          heading below the image (147's own deliberate placement); repeating it here would be a
          second identical-text h1 immediately above it. */}
      <div className="px-4 pt-4">
        <PageHeader title="Car Details" fallback="/search" />
      </div>
      <div className="aspect-video bg-surface-mid overflow-hidden">
        {images.length > 0 ? (
          <img
            src={images[activeImg]?.url}
            alt={car.label ?? car.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Car size={96} className="text-gray-600" aria-hidden="true" />
          </div>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 p-3 overflow-x-auto">
          {images.map((img, i) => (
            <button
              key={img.url}
              type="button"
              onClick={() => setActiveImg(i)}
              className={`shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 ${
                i === activeImg ? 'border-primary' : 'border-transparent'
              }`}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="px-4 py-4 flex flex-col gap-4">
        <div>
          {/* 052: the label, not `name` — the grid below already lists transmission, fuel,
              seats and year, so repeating them in the heading was noise. 147: the plate is
              public now, by explicit user decision — reverses 054's FR-009. Shown here alone
              (year stays only in the spec grid below, per the comment above — 146 had drifted
              from its own stated intent by repeating year here too; fixed as part of 147). */}
          <h1 className="text-2xl font-bold text-text">{car.label ?? car.name}</h1>
          {car.plate_number && <p className="mt-1 text-sm text-gray-500">{car.plate_number}</p>}
          {/* 146 (US1, FR-004): the rating now lives in the persistent booking bar, next to the
              price — not duplicated here, so a customer sees it exactly once, where the decision
              actually happens. */}
          {car.supplier && (
            <p className="mt-1 text-sm text-gray-400">
              Provided by <span className="text-text">{car.supplier.company_name}</span>
            </p>
          )}
          {car.description && <p className="mt-2 text-gray-400 text-sm">{car.description}</p>}
        </div>

        {/* 408 (mock-up screen 2): the fuel now, large, with when Cartrack read it. */}
        {car.fuel && (
          <div className="bg-surface-card rounded-xl p-4 shadow-lg">
            <FuelGauge fuel={car.fuel} size="large" />
          </div>
        )}

        {/* 052: where the car is picked up. This data was already on the wire and simply
            never shown (gap-list C3).
            401 (#401): the LOCATION NAME is the headline, not the city — a customer reading
            "Victoria" (the city) thought the car was physically in that town rather than at
            the named pickup point. `name` falls back to `city_name` for an older/cached
            response that predates 401, so this never renders nothing. */}
        {(car.location?.name || car.location?.city_name || car.location?.address_line_1) && (
          <div className="bg-surface-card rounded-xl p-4 shadow-lg">
            <h2 className="text-sm font-semibold text-gray-400 mb-1">Pickup</h2>
            {(car.location.name || car.location.city_name) && (
              <p className="text-sm font-medium text-text">
                {car.location.name || car.location.city_name}
              </p>
            )}
            {/* The city becomes a secondary line ONLY once the name is the headline — with no
                name, city_name already took the headline slot above and must not repeat. */}
            {car.location.name && car.location.city_name && (
              <p className="text-sm text-gray-400">{car.location.city_name}</p>
            )}
            {car.location.address_line_1 && (
              <p className="text-sm text-gray-400">{car.location.address_line_1}</p>
            )}
          </div>
        )}

        {/* 146 (US1, FR-003): icon-first spec rows — the research behind this feature found
            this the single clearest "reads as modern, not a spec sheet" signal.
            147: `color`/`category` added, conditionally — both are optional fields, unlike the
            original four, so an absent one is filtered out rather than rendered as a blank
            card (testing.md trap 2). */}
        <div className="grid grid-cols-2 gap-3">
          {(
            [
              ['transmission', 'Transmission', car.transmission],
              ['fuel', 'Fuel', car.fuel_type],
              ['seats', 'Seats', String(car.seat_capacity)],
              ['year', 'Year', String(car.year)],
              ['color', 'Color', car.color],
              ['category', 'Category', car.category_name],
            ] as const
          )
            .filter(([, , value]) => Boolean(value))
            .map(([key, label, value]) => {
              const Icon = SPEC_ICONS[key];
              return (
                <div
                  key={key}
                  data-testid={`spec-${key}`}
                  className="bg-surface-card rounded-lg p-3 flex items-center gap-3 shadow-lg"
                >
                  <Icon size={20} className="text-primary shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-sm font-medium text-text capitalize">{value}</p>
                  </div>
                </div>
              );
            })}
        </div>

        {car.features && car.features.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 mb-2">Features</h2>
            <div className="flex flex-wrap gap-2">
              {car.features.map((f) => (
                <Badge key={f.id} variant="info">
                  {f.icon && `${f.icon} `}
                  {f.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* 057: what renters said. Rendered only when the listing actually returned rows —
            a failed or empty review request leaves the rest of the page untouched. */}
        {reviewsFailed && (
          <p data-testid="car-reviews-error" className="text-sm text-error">
            {lookupFailedMessage('Reviews')}
          </p>
        )}

        {reviewItems.length > 0 && car.rating && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 mb-2">Reviews</h2>
            {/* 363 (designs 2c/2d): the average in half stars and, from 3 reviews, how they split. */}
            <div
              data-testid="reviews-summary"
              className="mb-3 bg-surface-card rounded-xl p-4 flex items-end justify-between gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <span className="text-3xl font-bold text-text leading-none">
                  {car.rating.average.toFixed(1)}
                </span>
                <StarRating value={car.rating.average} size={16} />
                <span className="text-sm text-gray-400">
                  {car.rating.count} {car.rating.count === 1 ? 'review' : 'reviews'}
                </span>
              </div>
              {car.rating.count >= SPLIT_FROM && car.rating.distribution && (
                <div className="w-48 flex flex-col gap-1">
                  {(['5', '4', '3', '2', '1'] as const).map((star) => {
                    const n = car.rating?.distribution?.[star] ?? 0;
                    const pct = car.rating?.count ? (n / car.rating.count) * 100 : 0;
                    return (
                      <div
                        key={star}
                        data-testid="reviews-split-row"
                        className="grid grid-cols-[0.75rem_minmax(0,1fr)_1.5rem] items-center gap-2 text-xs text-gray-400"
                      >
                        <span>{star}</span>
                        <div className="h-1.5 rounded bg-surface-alt overflow-hidden">
                          <div className="h-1.5 bg-primary" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-right">{n}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-3">
              {reviewItems.map((review) => (
                <div
                  key={review.id}
                  className="bg-surface-card rounded-xl p-4 flex flex-col gap-2 shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-text">
                      {review.author?.name ?? 'Anonymous'}
                    </span>
                    <span className="text-xs text-gray-400">{mytDate(review.created_at)}</span>
                  </div>
                  <StarRating value={review.rating} size={14} />
                  {review.comment && <p className="text-sm text-text">{review.comment}</p>}
                </div>
              ))}
            </div>
            {/* 363 (FR-013): "Showing 5 of 23" used to lead nowhere. */}
            {reviewTotal > reviewItems.length && (
              <a
                href={`/cars/${id}/reviews`}
                className="mt-3 flex h-11 items-center justify-center rounded-full border border-border text-sm font-semibold text-text"
              >
                See all {reviewTotal} reviews
              </a>
            )}
          </div>
        )}

        {hasCarriedContext && (
          <TripContextStrip
            locationName={car.location?.name || car.location?.city_name}
            pickup={tripPickup}
            return={tripReturn}
            occupiedWindows={occupied.windows}
            onChange={({ pickup, return: ret }) => {
              setTripPickup(pickup);
              setTripReturn(ret);
            }}
          />
        )}

        {/* 146 (US2, FR-005/FR-006/FR-007): already-known facts, surfaced for the first time.
            Absence of either renders nothing — never a placeholder or a zero (FR-007). */}
        {(operatingWindowText || minRentalText) && (
          <div
            data-testid="car-availability-info"
            className="bg-surface-card rounded-xl p-4 flex flex-col gap-1 shadow-lg"
          >
            {operatingWindowText && <p className="text-sm text-text">{operatingWindowText}</p>}
            {minRentalText && <p className="text-sm text-text">{minRentalText}</p>}
          </div>
        )}

        {/* 400 (#1354, mock-up screen 2): each promo by name, price per hour, hours and days. */}
        {(car.pricing?.promos?.length ?? 0) > 0 && (
          <section
            aria-labelledby="car-promos-heading"
            className="bg-surface-card rounded-xl p-4 flex flex-col gap-2 shadow-lg"
          >
            <h2
              id="car-promos-heading"
              className="text-xs font-semibold uppercase tracking-wide text-gray-400"
            >
              Promo prices
            </h2>
            {car.pricing?.promos?.map((p) => (
              <div
                key={`${p.name}-${p.start_time}`}
                data-testid="car-promo"
                className="flex items-start justify-between gap-3 border-t border-surface-mid pt-2 first-of-type:border-0 first-of-type:pt-0"
              >
                <div>
                  <p className="font-semibold text-text">{p.name}</p>
                  <p className="text-xs text-gray-400">{`${promoHoursWords(p)} · ${daysWords(p.days_of_week)}`}</p>
                </div>
                <p className="whitespace-nowrap font-bold text-primary">{`${formatMinor(p.per_hour_minor, symbol)}/hour`}</p>
              </div>
            ))}
            <p className="text-xs text-gray-400">
              Malaysia time. Applied automatically to the hours of your booking that fall in a
              promo.
            </p>
          </section>
        )}

        {/* 146 (US3, FR-008/FR-009): static, platform-wide — identical on every car's page. */}
        <div
          data-testid="car-good-to-know"
          className="bg-surface-card rounded-xl p-4 flex flex-col gap-3 shadow-lg"
        >
          <h2 className="text-sm font-semibold text-gray-400">Good to know</h2>
          {GOOD_TO_KNOW_ITEMS.map((item) => (
            <div key={item.heading}>
              <p className="text-sm font-medium text-text">{item.heading}</p>
              <p className="text-sm text-gray-400">{item.body}</p>
            </div>
          ))}
        </div>

        {/* 146 (US1, FR-002/FR-004): persistent price + Book Now, with the rating alongside it.
            `fixed`, not `sticky` (research.md Decision 3, corrected after a live defect —
            `sticky` on a last-child element overlapped the "Good to know" card above it). The
            trailing spacer below reserves room so this never permanently hides real content. */}
        <div
          data-testid="car-booking-bar"
          className="fixed bottom-20 left-0 right-0 z-20 mx-4 bg-surface-card rounded-xl p-4 flex items-center justify-between shadow-lg"
        >
          <div>
            {car.rating && (
              <div className="flex items-center gap-2 mb-1">
                <StarRating value={car.rating.average} size={14} />
                <span className="text-sm font-semibold text-text">
                  {car.rating.average.toFixed(1)}
                </span>
                <span className="text-xs text-gray-400">
                  {car.rating.count} {car.rating.count === 1 ? 'review' : 'reviews'}
                </span>
              </div>
            )}
            {hourlyRate ? (
              <>
                <p className="text-2xl font-bold text-primary">
                  {hourlyRate}
                  <span className="text-sm font-normal text-gray-400">/hour</span>
                </p>
                {dailyRate && <p className="text-sm text-gray-400">{dailyRate}/day</p>}
              </>
            ) : (
              dailyRate && (
                <p className="text-2xl font-bold text-primary">
                  {dailyRate}
                  <span className="text-sm font-normal text-gray-400">/day</span>
                </p>
              )
            )}
          </div>
          <Button onClick={handleBookNow}>Book Now</Button>
        </div>
        {/* Reserves room for the fixed bar above so it never permanently hides the last real
            content (the "Good to know" card) once scrolled all the way down. Generous enough
            for the bar's tallest state (price + rating). */}
        <div aria-hidden="true" className="h-28" />
      </div>
    </div>
  );
}
